import { AxiosResponse } from 'axios';
import {
    betExport,
    betList,
    refundBets,
    setBetResult,
    setResultPlayer,
    setResultUsingScore,
    skipAutomaticSettlement,
    unsetResultPlayer,
    voidBets,
} from '@/api/bet';
import { AppError } from '@/errors/appError';
import { baseListExportService, baseListService, isRequestCanceled } from '@/services/baseService';
import {
    IBetApiListResponse,
    IBetFilters,
    IListParams,
    IRefundBetParams,
    ISetBetResultParams,
    ISetResultPlayerParams,
    ISetResultUsingScoreParams,
    ISkipAutomaticSettlementParams,
    IUnsetResultPlayerParams,
    IVoidBetParams,
} from '@/types';

export const betService = {
    async list(params: IListParams<IBetFilters>): Promise<AxiosResponse<IBetApiListResponse>> {
        try {
            return await baseListService<IBetApiListResponse>(betList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Bet API Error:', e);
            throw new AppError('Unable to load bet list, request error', { cause: e });
        }
    },

    async export(params: IListParams<IBetFilters | Record<string, any>>): Promise<AxiosResponse<Blob>> {
        try {
            return await baseListExportService(betExport, params);
        } catch (e) {
            console.error('Bet Export API Error:', e);
            throw new AppError('Unable to export bets, request error', { cause: e });
        }
    },

    async setBetResult(params: ISetBetResultParams): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await setBetResult(params);
        } catch (e) {
            console.error('Bet SetResult API Error:', e);
            throw new AppError('Unable to set bet result, request error', { cause: e });
        }
    },

    async refundBets(params: { rows: IRefundBetParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await refundBets(params);
        } catch (e) {
            console.error('Bet Refund API Error:', e);
            throw new AppError('Unable to refund bets, request error', { cause: e });
        }
    },

    async voidBets(params: { rows: IVoidBetParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await voidBets(params);
        } catch (e) {
            console.error('Bet Void API Error:', e);
            throw new AppError('Unable to void bets, request error', { cause: e });
        }
    },

    async setResultPlayer(params: { rows: ISetResultPlayerParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await setResultPlayer(params);
        } catch (e) {
            console.error('Bet SetResultPlayer API Error:', e);
            throw new AppError('Unable to set result player, request error', { cause: e });
        }
    },

    async setResultUsingScore(params: { rows: ISetResultUsingScoreParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await setResultUsingScore(params);
        } catch (e) {
            console.error('Bet SetResultUsingScore API Error:', e);
            throw new AppError('Unable to set result using score, request error', { cause: e });
        }
    },

    async unsetResultPlayer(params: { rows: IUnsetResultPlayerParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await unsetResultPlayer(params);
        } catch (e) {
            console.error('Bet UnsetResultPlayer API Error:', e);
            throw new AppError('Unable to unset result player, request error', { cause: e });
        }
    },

    async skipAutomaticSettlement(params: { rows: ISkipAutomaticSettlementParams[] }): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await skipAutomaticSettlement(params);
        } catch (e) {
            console.error('Bet SkipAutomaticSettlement API Error:', e);
            throw new AppError('Unable to skip automatic settlement, request error', { cause: e });
        }
    },
};
